import React, { useEffect, useRef } from 'react';
import { Box, Container, Typography, Grid } from '@mui/material';
import { motion, useAnimation, useScroll, useTransform } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { armoredVehicle, gunComponents, aircraft } from '../assets';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const ExpertiseCard = ({ image, title, description, index }) => {
  const controls = useAnimation();
  const [ref, inView] = useInView({
    threshold: 0.2,
    triggerOnce: true
  });

  const cardVariants = {
    hidden: { 
      opacity: 0,
      y: 50,
      rotateY: -15
    },
    visible: {
      opacity: 1,
      y: 0,
      rotateY: 0,
      transition: {
        duration: 0.8,
        delay: index * 0.2,
        ease: "easeOut"
      }
    }
  };

  return (
    <motion.div
      ref={ref}
      initial="hidden"
      animate={inView ? "visible" : "hidden"}
      variants={cardVariants}
      style={{ height: '100%' }}
    >
      <Box
        sx={{
          backgroundColor: 'white',
          borderRadius: '20px',
          overflow: 'hidden',
          boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)',
          transition: 'all 0.4s ease-out',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
          '&:hover': {
            transform: 'translateY(-5px)',
            boxShadow: '0 20px 40px rgba(0, 0, 0, 0.2)',
            '& .image-overlay': {
              opacity: 0.7
            },
            '& .card-image': {
              transform: 'scale(1.1)'
            }
          }
        }}
      >
        <Box
          sx={{
            width: '100%',
            height: '300px',
            overflow: 'hidden',
            position: 'relative'
          }}
        >
          <Box
            component="img"
            src={image}
            alt={title}
            className="card-image"
            sx={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              transition: 'transform 0.6s ease-out'
            }}
          />
          <Box
            className="image-overlay"
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'linear-gradient(to bottom, rgba(26, 71, 42, 0), rgba(26, 71, 42, 0.8))',
              opacity: 0,
              transition: 'opacity 0.4s ease'
            }}
          />
        </Box>
        <Box 
          sx={{ 
            p: 4, 
            flex: 1, 
            display: 'flex', 
            flexDirection: 'column',
            position: 'relative',
            background: 'linear-gradient(135deg, rgba(255,255,255,1) 0%, rgba(26,71,42,0.05) 100%)'
          }}
        >
          <Typography
            variant="h5"
            sx={{
              color: '#1a472a',
              fontWeight: 700,
              mb: 2,
              minHeight: '64px',
              display: 'flex',
              alignItems: 'center',
              position: 'relative',
              '&::after': {
                content: '""',
                position: 'absolute',
                bottom: -8,
                left: 0,
                width: '60px',
                height: '3px',
                background: 'linear-gradient(90deg, #1a472a 0%, rgba(26,71,42,0.3) 100%)',
                borderRadius: '2px'
              }
            }}
          >
            {title}
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: 'rgba(26,71,42,0.8)',
              lineHeight: 1.8,
              flex: 1
            }}
          >
            {description}
          </Typography>
        </Box>
      </Box>
    </motion.div>
  );
};

const Expertise = () => {
  const containerRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start end", "end start"]
  });

  const backgroundY = useTransform(scrollYProgress, [0, 1], ['0%', '100%']);
  const opacity = useTransform(scrollYProgress, [0, 0.3, 0.8, 1], [0, 1, 1, 0]);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.to(".parallax-bg", {
        backgroundPosition: "50% 100%",
        ease: "none",
        scrollTrigger: {
          trigger: containerRef.current,
          start: "top center",
          end: "bottom center",
          scrub: true
        }
      });
    }, containerRef);

    return () => ctx.revert();
  }, []);

  const expertiseData = [
    {
      image: armoredVehicle,
      title: 'Armored Vehicle Components',
      description: 'Specialized in manufacturing high-grade armor plates, protective components, and critical vehicle parts that meet international defense standards.'
    },
    {
      image: gunComponents,
      title: 'Precision Gun Components',
      description: 'Production of precise firearm components with advanced machining capabilities, ensuring reliability and accuracy in every piece.'
    },
    {
      image: aircraft,
      title: 'Aircraft Parts Manufacturing',
      description: 'Expert manufacturing of aircraft components with strict adherence to aerospace quality standards and specifications.'
    }
  ];

  return (
    <Box
      ref={containerRef}
      sx={{
        position: 'relative',
        py: { xs: 8, md: 12 },
        overflow: 'hidden'
      }}
    >
      <Box
        className="parallax-bg"
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: 'url(/path/to/gear-pattern.png)',
          opacity: 0.05,
          zIndex: 0
        }}
      />
      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <Typography
            variant="h2"
            align="center"
            sx={{
              color: '#1a472a',
              fontWeight: 700,
              mb: 2,
              position: 'relative',
              '&::after': {
                content: '""',
                position: 'absolute',
                bottom: -16,
                left: '50%',
                transform: 'translateX(-50%)',
                width: '120px',
                height: '4px',
                background: 'linear-gradient(90deg, rgba(26,71,42,0.3) 0%, #1a472a 50%, rgba(26,71,42,0.3) 100%)',
                borderRadius: '2px'
              }
            }}
          >
            Our Expertise
          </Typography>
          <Typography
            variant="h6"
            align="center"
            sx={{
              color: 'rgba(26,71,42,0.8)',
              mb: 8,
              maxWidth: '800px',
              mx: 'auto',
              lineHeight: 1.8
            }}
          >
            Delivering precision-engineered solutions across defense and aerospace sectors
          </Typography>
        </motion.div>

        <Grid container spacing={4}>
          {expertiseData.map((item, index) => (
            <Grid item xs={12} md={4} key={index}>
              <ExpertiseCard {...item} index={index} />
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};

export default Expertise;
